import { FabricObject, loadSVGFromURL, util } from 'fabric';
import type { CanvasJSON } from '../../canvas/types';

export interface SvgUrlMarker {
  kind: 'svg-url';
  src: string;
  pieceId?: string;
  left: number;
  top: number;
  originX?: 'left' | 'center' | 'right';
  originY?: 'top' | 'center' | 'bottom';
  targetWidth?: number;
  targetHeight?: number;
  scaleX?: number;
  scaleY?: number;
  selectable?: boolean;
  evented?: boolean;
  blendMode?: GlobalCompositeOperation;
  opacity?: number;
  clipPath?: JsonObject;
}

type JsonObject = Record<string, unknown>;

export function isSvgUrlMarker(obj: unknown): obj is SvgUrlMarker {
  return (
    obj != null &&
    typeof obj === 'object' &&
    (obj as { kind?: unknown }).kind === 'svg-url' &&
    typeof (obj as { src?: unknown }).src === 'string'
  );
}

// One in-flight fetch per URL, shared across resolutions and re-loads in the same session.
const cache = new Map<string, Promise<FabricObject>>();

async function loadAsFabricObject(src: string): Promise<FabricObject> {
  let p = cache.get(src);
  if (!p) {
    p = (async () => {
      const { objects, options } = await loadSVGFromURL(src, undefined, { crossOrigin: 'anonymous' });
      const valid = objects.filter((o): o is FabricObject => o !== null);
      if (valid.length === 0) throw new Error(`svg-url marker: no objects parsed from ${src}`);
      return util.groupSVGElements(valid, options);
    })();
    cache.set(src, p);
  }
  return p;
}

function placeholderFor(marker: SvgUrlMarker): JsonObject {
  return {
    type: 'Rect',
    version: '7.1.0',
    pieceId: marker.pieceId,
    left: marker.left,
    top: marker.top,
    originX: marker.originX ?? 'center',
    originY: marker.originY ?? 'center',
    width: marker.targetWidth ?? 0,
    height: marker.targetHeight ?? 0,
    fill: 'transparent',
    stroke: null,
    strokeWidth: 0,
    selectable: marker.selectable ?? true,
    evented: marker.evented ?? true
  };
}

async function hydrateMarker(marker: SvgUrlMarker): Promise<JsonObject> {
  let shape: FabricObject;
  try {
    shape = await loadAsFabricObject(marker.src);
  } catch (err) {
    console.warn(`[hydrate-svg-urls] ${marker.src} failed; using transparent placeholder`, err);
    return placeholderFor(marker);
  }

  const json = shape.toObject() as JsonObject;

  json.left = marker.left;
  json.top = marker.top;
  json.originX = marker.originX ?? 'center';
  json.originY = marker.originY ?? 'center';

  if (marker.pieceId !== undefined) json.pieceId = marker.pieceId;
  if (marker.selectable !== undefined) json.selectable = marker.selectable;
  if (marker.evented !== undefined) json.evented = marker.evented;
  if (marker.blendMode !== undefined) json.globalCompositeOperation = marker.blendMode;
  if (marker.opacity !== undefined) json.opacity = marker.opacity;
  if (marker.clipPath !== undefined) json.clipPath = marker.clipPath;

  if (marker.scaleX !== undefined) {
    json.scaleX = marker.scaleX;
  } else if (marker.targetWidth && typeof json.width === 'number' && json.width > 0) {
    json.scaleX = marker.targetWidth / json.width;
  }
  if (marker.scaleY !== undefined) {
    json.scaleY = marker.scaleY;
  } else if (marker.targetHeight && typeof json.height === 'number' && json.height > 0) {
    json.scaleY = marker.targetHeight / json.height;
  }

  return json;
}

/** Replaces every `{ kind: 'svg-url', src }` marker with its parsed Path/Group JSON. */
export async function hydrateSvgUrlsInObjects<T extends Record<string, unknown>>(objects: T[]): Promise<T[]> {
  return Promise.all(
    objects.map(async obj => {
      if (isSvgUrlMarker(obj)) return hydrateMarker(obj) as Promise<T>;
      const nested = (obj as { objects?: unknown }).objects;
      if (Array.isArray(nested)) {
        return { ...obj, objects: await hydrateSvgUrlsInObjects(nested as JsonObject[]) } as T;
      }
      return obj;
    })
  );
}

/** Same as `hydrateSvgUrlsInObjects` but takes a full CanvasJSON. */
export async function hydrateSvgUrlsInJson(json: CanvasJSON): Promise<CanvasJSON> {
  const objects = await hydrateSvgUrlsInObjects(json.objects as JsonObject[]);
  return { ...json, objects };
}

/** For tests / dev. Drops the in-memory cache. */
export function clearSvgUrlCache(): void {
  cache.clear();
}
